import { isServer, restoreUnicodeCharacters, truncateString } from '@/helpers';
import { _t } from '@/locale';
import { useEffect, useRef, useState } from 'react';

import { classnames } from '@/helpers';

type ReadMoreProps = {
  text: string;
  expanded?: boolean;
  maxLines?: number;
  maxCharacters?: number;
  className?: string;
  textClassName?: string;
  bottomSlot?: React.ReactNode;
  onShowReadMore?: () => void;
};

const ReadMore = ({
  expanded = false,
  text,
  maxLines,
  maxCharacters,
  bottomSlot,
  className = 'text-sm',
  textClassName = '',
  onShowReadMore,
}: ReadMoreProps) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [needsTruncation, setNeedsTruncation] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    if (!elementRef?.current) {
      return;
    }

    const lineHeight = parseFloat(getComputedStyle(elementRef.current).lineHeight);
    const maxHeight = lineHeight * maxLines;
    setNeedsTruncation(elementRef.current.scrollHeight > maxHeight || Boolean(bottomSlot));
  }, [text, maxLines, bottomSlot]);

  const handleToggleTruncate = () => {
    setIsExpanded((isExpanded) => !isExpanded);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onShowReadMore) {
      onShowReadMore();
    } else {
      handleToggleTruncate();
    }
  };

  const getMarkup = () => {
    if (!text) {
      return null;
    }

    // Decoded before measuring, so an entity like &amp; counts as one character and is never cut in half.
    const decodedText = isServer ? text : restoreUnicodeCharacters(text);

    if (maxCharacters && decodedText.length > maxCharacters) {
      const truncatedText = isExpanded ? decodedText : truncateString(decodedText, maxCharacters);

      return (
        <div className={className}>
          <p className={classnames('inline whitespace-pre-wrap', { [textClassName]: textClassName })}>
            {truncatedText}
          </p>{' '}
          <button
            className={classnames('!text-fg_link underline outline-none', { [textClassName]: textClassName })}
            onClick={handleClick}>
            {isExpanded ? _t('showLess') : _t('readMore')}
          </button>
        </div>
      );
    }

    if (maxLines) {
      return (
        <div
          className={`${className} ${maxLines === 1 ? 'flex items-start' : ''} ${
            isExpanded ? 'whitespace-pre-wrap' : ''
          }`}>
          <div
            ref={elementRef}
            style={{
              display: isExpanded ? 'block' : '-webkit-box',
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              WebkitLineClamp: isExpanded ? 'unset' : maxLines,
              textOverflow: 'ellipsis',
            }}>
            {isServer ? text : restoreUnicodeCharacters(text)}
          </div>

          {isExpanded && bottomSlot && <div className="mt-sm">{bottomSlot}</div>}
          {needsTruncation && (
            <button
              className={`text-fg_link underline outline-none ${maxLines === 1 ? 'whitespace-nowrap' : 'pt-sm'}`}
              onClick={handleClick}>
              {isExpanded ? _t('showLess') : _t('readMore')}
            </button>
          )}
        </div>
      );
    }

    return (
      <div className={className}>
        <p className={classnames({ [textClassName]: textClassName })}>
          {isServer ? text : restoreUnicodeCharacters(text)}
        </p>
      </div>
    );
  };

  return getMarkup();
};

export default ReadMore;
